import { useMemo } from "react";
import { Link } from "wasp/client/router";
import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";
import { formatSessionDuration, isTradeInSession } from "../../../trading-sessions/sessionUtils";

function fmt(v: number): string {
  const abs = Math.abs(v);
  const prefix = v < 0 ? "-$" : "$";
  if (abs >= 1000) return `${prefix}${(abs / 1000).toFixed(1)}k`;
  return `${prefix}${abs.toFixed(0)}`;
}

function formatDate(date: Date | string): string {
  return new Date(date).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function computeSessionPnl(session: any, trades: any[]): { pnl: number; tradeCount: number } {
  if (!session.startedAt || !session.endedAt) return { pnl: 0, tradeCount: 0 };
  // Attach trades to the session by their ENTRY time (see isTradeInSession).
  const sessionTrades = trades.filter((tr) => isTradeInSession(tr, session));
  const pnl = sessionTrades.reduce((sum, tr) => sum + (tr.pnl ?? 0) + (tr.commission ?? 0) + (tr.swaps ?? 0), 0);
  return { pnl: Math.round(pnl * 100) / 100, tradeCount: sessionTrades.length };
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground p-4">
      {label}
    </div>
  );
}

export default function SessionsListWidget() {
  const { sessions, statsTradesList, isDark, t } = useDashboardData();

  const rows = useMemo(() => {
    const completed = (sessions ?? []).filter((s: any) => !s.isActive && s.startedAt && s.endedAt);
    return completed.map((s: any) => {
      const { pnl, tradeCount } = computeSessionPnl(s, statsTradesList ?? []);
      return { session: s, pnl, tradeCount };
    });
  }, [sessions, statsTradesList]);

  const headerClass = "text-[10px] font-semibold tracking-widest uppercase text-muted-foreground";
  const borderClass = isDark ? "border-border" : "border-border";

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader
        label={t.analytics.sessionsLabel ?? "Sessions"}
        isDark={isDark}
      />
      <CardContent className="px-0 pb-0 pt-0 flex-1 overflow-hidden flex flex-col">
        {rows.length === 0 ? (
          <EmptyState label={t.analytics.sessionsListNoSessions} />
        ) : (
          <div className="overflow-y-auto flex-1">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${borderClass}`}>
                  <th className={`text-left px-4 py-2 ${headerClass}`}>{t.analytics.sessionsListName}</th>
                  <th className={`text-left px-4 py-2 ${headerClass}`}>{t.analytics.sessionsListDate}</th>
                  <th className={`text-left px-4 py-2 ${headerClass}`}>{t.analytics.sessionsListDuration}</th>
                  <th className={`text-right px-4 py-2 ${headerClass}`}>{t.analytics.sessionsListTrades}</th>
                  <th className={`text-right px-4 py-2 ${headerClass}`}>{t.analytics.sessionsListPnl}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ session, pnl, tradeCount }) => (
                  <tr
                    key={session.id}
                    className={`border-b ${borderClass} hover:bg-muted/40 transition-colors`}
                  >
                    <td className="px-4 py-2 max-w-[12rem]">
                      <Link
                        to="/session/:sessionId"
                        params={{ sessionId: session.id }}
                        className="block truncate text-primary hover:underline"
                      >
                        {session.name || formatDate(session.startedAt)}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">
                      {formatDate(session.startedAt)}
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">
                      {formatSessionDuration(session.actualDuration)}
                    </td>
                    <td className="px-4 py-2 text-right font-mono">{tradeCount}</td>
                    <td className={`px-4 py-2 text-right font-mono font-semibold ${pnl > 0 ? "text-pnlPositive" : pnl < 0 ? "text-pnlNegative" : "text-muted-foreground"}`}>
                      {pnl !== 0 ? fmt(pnl) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
